import React, { useState, useEffect } from 'react';
import LocationList from '../../LocationList';
import ModeHelpTooltip from './components/ModeHelpTooltip';
import PathInfoDisplay from './components/PathInfoDisplay';
import ComparisonResults from './components/ComparisonResults';
import DetailedComparison from './components/DetailedComparison';
import { calculatePathDetails } from './utils/pathCalculations';
import { calculatePathDifference } from './utils/efficiencyCalculations';
import { useTp } from '../../../context/TpContext';
import '../../styles/modes/compareMode.css';

export default function CompareMode() {
  const { 
    locations, startId, autoPath, customPath, visiblePaths
  } = useTp();
  
  const [autoPathInfo, setAutoPathInfo] = useState(null);
  const [customPathInfo, setCustomPathInfo] = useState(null);
  const [pathDifference, setPathDifference] = useState(null);
  const [autoSegments, setAutoSegments] = useState([]);
  const [customSegments, setCustomSegments] = useState([]);
  const [expandedDetails, setExpandedDetails] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Toggle help tooltip
  const toggleHelp = (e) => {
    e.preventDefault();
    setShowHelp(!showHelp);
  };

  // Calculate path information
  useEffect(() => {
    // Calculate auto path information
    if (autoPath && ((Array.isArray(autoPath) && autoPath.length > 1) || autoPath.type === 'FeatureCollection')) {
      if (autoPath.type === 'FeatureCollection') {
        // GeoJSON format (from algorithm solution) - individual flows
        const totalDistance = autoPath.features?.reduce((total, feature) => {
          const featureDistance = feature.properties?.distance || feature.properties?.total_distance || 0;
          return total + featureDistance;
        }, 0) || 0;
        
        setAutoPathInfo({
          distance: totalDistance,
          flowCount: autoPath.features?.length || 0,
          type: 'algorithm'
        });
      } else {
        // Array format (traditional)
        const { distance, segments } = calculatePathDetails(autoPath, locations);
        setAutoPathInfo({
          distance: distance,
          pointCount: autoPath.length,
          complete: autoPath.length > 0 && autoPath[0] === autoPath[autoPath.length - 1]
        });
        setAutoSegments(segments);
      }
    } else {
      setAutoPathInfo(null);
      setAutoSegments([]);
    }

    // Calculate custom path information
    if (customPath && ((Array.isArray(customPath) && customPath.length > 1) || customPath.type === 'FeatureCollection')) {
      if (customPath.type === 'FeatureCollection') {
        // GeoJSON format (from route editor) - individual flows
        const totalDistance = customPath.features?.reduce((total, feature) => {
          const featureDistance = feature.properties?.distance || 0;
          return total + featureDistance;
        }, 0) || 0;
        
        setCustomPathInfo({
          distance: totalDistance,
          flowCount: customPath.features?.length || 0,
          type: 'manual'
        });
      } else {
        // Array format (traditional)
        const { distance, segments } = calculatePathDetails(customPath, locations);
        setCustomPathInfo({
          distance: distance,
          pointCount: customPath.length,
          complete: customPath.length > 0 && customPath[0] === customPath[customPath.length - 1]
        });
        setCustomSegments(segments);
      }
    } else {
      setCustomPathInfo(null);
      setCustomSegments([]);
    }
  }, [autoPath, customPath, locations, visiblePaths]);

  // Calculate path difference
  useEffect(() => {
    if (autoPathInfo && customPathInfo) {
      setPathDifference(calculatePathDifference(customPathInfo, autoPathInfo));
    } else {
      setPathDifference(null);
    }
  }, [autoPathInfo, customPathInfo]);
  
  // Toggle detailed comparison display
  const toggleDetails = () => {
    setExpandedDetails(!expandedDetails);
  };

  return (
    <div className="compare-mode-container">
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Compare Mode</h3>
          <button 
            className="mode-help-button"
            onClick={toggleHelp}
            title="Help for Compare Paths mode"
          >
            ?
          </button>
        </div>
        <p>Compare performance differences between auto and custom paths.</p>
      </div>
      
      {/* Help tooltip */}
      {showHelp && <ModeHelpTooltip onClose={() => setShowHelp(false)} />}
      
      <div className="comparison-panel">
        <h4>Path Comparison</h4>
        
        {(!autoPathInfo && !customPathInfo) && (
          <div className="empty-paths-message">
            <p>Please create paths in "Auto Find Path" and "Custom Path" modes first, then compare them.</p>
          </div>
        )}
        
        {(autoPathInfo || customPathInfo) && (
          <div className="paths-comparison">
            <div className="path-info-container">
              <PathInfoDisplay 
                pathInfo={autoPathInfo}
                path={autoPath || []}
                locations={locations}
                type="auto"
              />
              
              <PathInfoDisplay 
                pathInfo={customPathInfo}
                path={customPath || []}
                locations={locations}
                type="custom"
              />
            </div>
            
            <ComparisonResults
              pathDifference={pathDifference}
              autoPathInfo={autoPathInfo}
              customPathInfo={customPathInfo}
              expandedDetails={expandedDetails}
              toggleDetails={toggleDetails}
            />
            
            <DetailedComparison
              expandedDetails={expandedDetails}
              autoSegments={autoSegments}
              customSegments={customSegments}
              autoPathInfo={autoPathInfo}
              customPathInfo={customPathInfo}
              pathDifference={pathDifference}
            />
          </div>
        )}
      </div>
    </div>
  );
} 